<script lang="ts" setup>
import { useUnmanagedBusinessStore } from '@/unmanagedBusinessModule/pages/unmanagedBusinessStore';
import { storeToRefs } from 'pinia';
import { i18n } from '@/i18n';
import TextInput from './TextInput.vue';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import SimpleSelectInput from '@/corePagesModule/components/SelectInput.vue';
import Button from '@/components/input/Btn/Btn.vue';
import { computed, watch } from 'vue';
import { AggregateBusinessRepository } from '@/coreInfrastructureModule/Repositories/AggregateBusinessRepository';
import { useForms } from '@/corePagesModule/composables/useForm';
import { validateRuleKeyMap, selectOptionKeyMap } from '../constants/formKeyMap';
import NewBaseTableContent from '@/corePagesModule/BaseTableContent/NewBaseTableContent.vue';
import { UnmanagedBusinessService } from '../domains/services/UnmanagedBusinessService';
import { FieldItem, ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import { RegisterBusinessRepository } from '@/coreInfrastructureModule/Repositories/RegisterBusinessRepository';
import { UnmanagedBusinessRepository } from '@/coreInfrastructureModule/Repositories/UnmanagedBusinessRepository';

const t = i18n.t;

const store = useUnmanagedBusinessStore();
const { state } = storeToRefs(store);
const { openModalByPreviousModal, openModal } = store;

const aggregateBusinessRepository = new AggregateBusinessRepository();
const registerBusinessRepository = new RegisterBusinessRepository();

const filedItems = computed((): FieldItem[] => {
  // 選択肢の項目を取得
  const items: FieldItem[] = [];
  Object.keys(state.value.modalInfo.modalState.checkDuplication.diffInfo).forEach((item) => {
    items.push({
      name: item,
      rules: validateRuleKeyMap[item],
    });
  });
  return items;
});

const { fieldState, allValidate, resetForm } = useForms(filedItems.value);

/**
 * 次へボタンクリック時の処理
 */
const onNext = () => {
  if (allValidate()) {
    state.value.settingUnmanagedBusiness.leadTime = Number(fieldState['leadTime'].value.value);
    state.value.settingUnmanagedBusiness.leadTimeUnit = UnmanagedBusinessService.convertUnitToSystemUnit(
      fieldState['leadTimeUnit'].value.value
    );
    state.value.settingUnmanagedBusiness.moq = Number(fieldState['MOQ'].value.value);
    state.value.settingUnmanagedBusiness.unit = fieldState['unit'].value.value;
    state.value.settingUnmanagedBusiness.stdInventoryIndices = {
      標準在庫指数: {
        min: Number(fieldState['stdInventoryIndicesMin'].value.value),
        max: Number(fieldState['stdInventoryIndicesMax'].value.value),
        option: {
          unit: UnmanagedBusinessService.convertUnitToSystemUnit(fieldState['stdInventoryIndicesUnit'].value.value),
        },
      },
    };
    openModalByPreviousModal('questionUpdateExistBusinessDiff', aggregateBusinessRepository, registerBusinessRepository);
  }
};

/**
 * 更新せず次へボタンクリック時の処理
 */
const onNextWhenNoUpdate = () => {
  state.value.settingUnmanagedBusiness.leadTime = state.value.modalInfo.modalState.checkDuplication.diffInfo.leadTime;
  state.value.settingUnmanagedBusiness.leadTimeUnit = state.value.modalInfo.modalState.checkDuplication.diffInfo.leadTimeUnit;
  state.value.settingUnmanagedBusiness.moq = state.value.modalInfo.modalState.checkDuplication.diffInfo.moq;
  state.value.settingUnmanagedBusiness.unit = state.value.modalInfo.modalState.checkDuplication.diffInfo.unit;
  state.value.settingUnmanagedBusiness.stdInventoryIndices = {
    標準在庫指数: {
      min: state.value.modalInfo.modalState.checkDuplication.diffInfo.stdInventoryIndicesMin,
      max: state.value.modalInfo.modalState.checkDuplication.diffInfo.stdInventoryIndicesMax,
      option: {
        unit: state.value.modalInfo.modalState.checkDuplication.diffInfo.stdInventoryIndicesUnit,
      },
    },
  };
  openModalByPreviousModal('questionUpdateExistBusinessDiff', aggregateBusinessRepository);
};

watch(
  () => state.value.modalInfo.isOpen.questionUpdateExistBusinessDiff,
  (value) => {
    if (value) {
      resetForm(filedItems.value);
      // settingUnmanagedBusinessの値をfieldStateにセット
      for (let i = 0; i < filedItems.value.length; i++) {
        const name = filedItems.value[i].name;
        fieldState[name].setValue(state.value.modalInfo.modalState.registerLeadTimeAndEtc[name], false);
      }
    }
  }
);

/**
 * バリデーションにRequiredがある場合は*を返す
 * @param key　- 入力項目のキー
 */
const getRequiredMark = (key: string | number) => {
  if (validateRuleKeyMap[key].includes(ValidateRule.Required)) {
    return '*';
  }
  return '';
};
</script>
<template>
  <InfoModal
    :modal-state="state.modalInfo.isOpen.questionUpdateExistBusinessDiff"
    :back-button-label="t('戻る')"
    :next-button-label="t('更新する')"
    @on-back="openModal('detectDuplication')"
    @on-next="onNext"
  >
    <template #title>{{ t('リードタイム、標準在庫指数、単位を更新しますか？') }}</template>
    <template #description>{{ t('すでに登録されていたをリードタイム、標準在庫指数、単位を更新することができます。') }}</template>
    <div>
      <p class="modal__required-text">{{ t('*マークは必須') }}</p>
      <div class="modal__selection modal__selection_table">
        <NewBaseTableContent
          :prop-table-id="'RegisterUpdateInfoModal'"
          :prop-table-content-properties="state.tableInfo.modal.tableContentProperties"
          :prop-display-objects="state.tableInfo.modal.tableContentItems"
          :prop-loading-status="state.tableInfo.modal.loadingState"
          :prop-table-type="'unmanagedBusinessTypeModal'"
          class="modal__table"
        />
      </div>
    </div>
    <div>
      <p class="modal__sub-title">{{ t('既存登録情報') }}</p>
      <div class="modal__selection">
        <!-- リードタイム・リードタイム単位・MOQ・単位・標準在庫指数下限・標準在庫指数上限・標準在庫指数単位 を値が入った状態かつdisableで表示する -->
        <TextInput
          :model-value="state.modalInfo.modalState.checkDuplication.diffInfo.leadTime.toString()"
          :label="t('取引登録モーダル選択肢:leadTime') + getRequiredMark('leadTime')"
          placeholder=""
          :disabled="true"
        />
        <TextInput
          :model-value="
            UnmanagedBusinessService.convertSystemUnitToDisplayUnit(
              state.modalInfo.modalState.checkDuplication.diffInfo.leadTimeUnit.toString()
            )
          "
          :label="t('取引登録モーダル選択肢:leadTimeUnit') + getRequiredMark('leadTimeUnit')"
          placeholder=""
          :disabled="true"
        />
        <TextInput
          :model-value="state.modalInfo.modalState.checkDuplication.diffInfo.moq.toString()"
          :label="t('取引登録モーダル選択肢:MOQ') + getRequiredMark('MOQ')"
          placeholder=""
          :disabled="true"
        />
        <TextInput
          :model-value="state.modalInfo.modalState.checkDuplication.diffInfo.unit?.toString() || ''"
          :label="t('取引登録モーダル選択肢:unit') + getRequiredMark('unit')"
          placeholder=""
          :disabled="true"
        />
        <TextInput
          :model-value="state.modalInfo.modalState.checkDuplication.diffInfo.stdInventoryIndicesMin.toString()"
          :label="t('取引登録モーダル選択肢:stdInventoryIndicesMin') + getRequiredMark('stdInventoryIndicesMin')"
          placeholder=""
          :disabled="true"
        />
        <TextInput
          :model-value="state.modalInfo.modalState.checkDuplication.diffInfo.stdInventoryIndicesMax.toString()"
          :label="t('取引登録モーダル選択肢:stdInventoryIndicesMax') + getRequiredMark('stdInventoryIndicesMax')"
          placeholder=""
          :disabled="true"
        />
        <TextInput
          :model-value="
            UnmanagedBusinessService.convertSystemUnitToDisplayUnit(
              state.modalInfo.modalState.checkDuplication.diffInfo.stdInventoryIndicesUnit.toString()
            )
          "
          :label="t('取引登録モーダル選択肢:stdInventoryIndicesUnit') + getRequiredMark('stdInventoryIndicesUnit')"
          placeholder=""
          :disabled="true"
        />
      </div>
    </div>
    <div>
      <p class="modal__sub-title">{{ t('更新情報') }}</p>

      <div class="modal__selection">
        <!-- リードタイム・リードタイム単位・MOQ・単位・標準在庫指数下限・標準在庫指数上限・標準在庫指数単位 の入力項目 -->
        <TextInput
          :model-value="fieldState.leadTime.value.value"
          :label="t('取引登録モーダル選択肢:leadTime') + getRequiredMark('leadTime')"
          :is-error="fieldState.leadTime.isError.value"
          :error-message="fieldState.leadTime.errorMessage.value"
          :placeholder="t('取引登録モーダル選択肢:leadTime')"
          @update:model-value="(e) => fieldState.leadTime.setValue(e)"
        />
        <SimpleSelectInput
          :model-value="fieldState.leadTimeUnit.value.value"
          :index="4"
          :label="t('取引登録モーダル選択肢:leadTimeUnit') + getRequiredMark('leadTimeUnit')"
          :is-error="fieldState.leadTimeUnit.isError.value"
          :error-message="fieldState.leadTimeUnit.errorMessage.value"
          :placeholder="t('取引登録モーダル選択肢:leadTimeUnit')"
          :options="selectOptionKeyMap.leadTimeUnit.map((item) => t(item))"
          @update:model-value="(e) => fieldState.leadTimeUnit.setValue(e)"
        />
        <TextInput
          :model-value="fieldState.moq.value.value"
          :label="t('取引登録モーダル選択肢:MOQ') + getRequiredMark('MOQ')"
          :is-error="fieldState.moq.isError.value"
          :error-message="fieldState.moq.errorMessage.value"
          :placeholder="t('取引登録モーダル選択肢:MOQ')"
          @update:model-value="(e) => fieldState.moq.setValue(e)"
        />
        <TextInput
          :model-value="fieldState.unit.value.value"
          :label="t('取引登録モーダル選択肢:unit') + getRequiredMark('unit')"
          :is-error="fieldState.unit.isError.value"
          :error-message="fieldState.unit.errorMessage.value"
          :placeholder="t('取引登録モーダル選択肢:unit')"
          @update:model-value="(e) => fieldState.unit.setValue(e)"
        />
        <TextInput
          :model-value="fieldState.stdInventoryIndicesMin.value.value"
          :label="t('取引登録モーダル選択肢:stdInventoryIndicesMin') + getRequiredMark('stdInventoryIndicesMin')"
          :is-error="fieldState.stdInventoryIndicesMin.isError.value"
          :error-message="fieldState.stdInventoryIndicesMin.errorMessage.value"
          :placeholder="t('取引登録モーダル選択肢:stdInventoryIndicesMin')"
          @update:model-value="(e) => fieldState.stdInventoryIndicesMin.setValue(e)"
        />
        <TextInput
          :model-value="fieldState.stdInventoryIndicesMax.value.value"
          :label="t('取引登録モーダル選択肢:stdInventoryIndicesMax') + getRequiredMark('stdInventoryIndicesMax')"
          :is-error="fieldState.stdInventoryIndicesMax.isError.value"
          :error-message="fieldState.stdInventoryIndicesMax.errorMessage.value"
          :placeholder="t('取引登録モーダル選択肢:stdInventoryIndicesMax')"
          @update:model-value="(e) => fieldState.stdInventoryIndicesMax.setValue(e)"
        />
        <SimpleSelectInput
          :model-value="fieldState.stdInventoryIndicesUnit.value.value"
          :index="6"
          :label="t('取引登録モーダル選択肢:stdInventoryIndicesUnit') + getRequiredMark('stdInventoryIndicesUnit')"
          :is-error="fieldState.stdInventoryIndicesUnit.isError.value"
          :error-message="fieldState.stdInventoryIndicesUnit.errorMessage.value"
          :placeholder="t('取引登録モーダル選択肢:stdInventoryIndicesUnit')"
          :options="selectOptionKeyMap.stdInventoryIndicesUnit.map((item) => t(item))"
          @update:model-value="(e) => fieldState.stdInventoryIndicesUnit.setValue(e)"
        />
      </div>
    </div>
    <template #betweenButtons>
      <Button :class="`modal__button modal__button--save`" @click="onNextWhenNoUpdate">
        {{ t('更新せず次へ') }}
      </Button>
    </template>
  </InfoModal>
</template>
<style lang="scss" scoped>
.modal {
  display: grid;
  gap: 24px;
  padding: 24px 16px;
  background-color: $semanticWhite;
  border-radius: 16px;
  min-width: 1300px;

  &__table {
    max-height: 300px !important;
    min-height: 0;
  }

  &__sub-title {
    font-family: Noto Sans JP;
    font-size: 16px;
    font-weight: 400;
    line-height: 16px;
    text-align: left;

    margin-bottom: 8px;

    color: $textDefault;
  }

  &__required-text {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 18.2px;
    text-align: left;
    color: $textDefault;
  }

  &__title {
    font-family: Noto Sans JP;
    font-size: 22px;
    font-weight: 700;
    line-height: 28.6px;
    text-align: center;

    color: $textDefault;
  }

  &__description {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 18.2px;
    text-align: center;

    color: $textDefault;
  }

  &__selection {
    background-color: $backgroundDefault;
    padding: 16px;
    border-radius: 8px;

    display: grid;
    grid-template-columns: repeat(4, 1fr);
    column-gap: 8px;
    row-gap: 16px;
    min-width: 1300px;

    &-user-input {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      column-gap: 8px;
      row-gap: 16px;
    }

    &_table {
      display: block;
    }
  }

  &__new-selection {
    background-color: $backgroundDefault;
    padding: 16px;
    border-radius: 8px;

    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 14px;
    text-align: left;

    &__container {
      display: grid;
      grid-template-columns: minmax(160px, auto) auto minmax(160px, auto) auto auto;
      column-gap: 8px;
      justify-content: start;
      align-items: center;
      margin: 8px 0 16px 0;
    }
  }

  &__button {
    min-width: 160px;
    height: 40px;
    font-family: Noto Sans JP;
    font-size: 16px;
    font-weight: 700;
    line-height: 40px;
    letter-spacing: 0em;
    text-align: center;
    border-radius: 4px;
    padding: 0 8px;
    box-shadow: none;

    &--cancel {
      color: $semanticPrimary;
      background-color: $semanticWhite;
      border: 1px solid $semanticPrimary;
    }
    &--save {
      color: $semanticWhite;
      background-color: $semanticPrimary;
      border: 1px solid $semanticPrimary;

      &:disabled {
        background-color: $surfaceSecondary;
        border: 1px solid $surfaceSecondary;
        color: $textDisable;
      }
    }

    &-container {
      display: grid;
      gap: 10px;
      grid-template-columns: repeat(2, auto);
      justify-content: center;
      padding-top: 16px;
    }
  }
}
</style>
